import React, { useEffect, useMemo, useState } from 'react';
import { Pencil, Trash2, RotateCcw } from 'lucide-react';
import CreateBookingLeadModal from '../CreateBookingLeadModal/CreateBookingLeadModal';
import BookingQuoteWaModal from '../BookingQuoteWaModal/BookingQuoteWaModal';
import CustomerVoiceCallModal from '../CustomerVoiceCallModal/CustomerVoiceCallModal';
import {
  postMemberAction,
  sendMemberWhatsapp,
  memberLastWaPatchFromSendResult,
  memberPatchFromAutoWhatsapp,
  memberRowTone,
  outcomeFromAction,
  hasStatusOutcome,
  isLeadOnlyMember,
  canSendWhatsappForMember,
} from '../../utils/marketingApi';
import { BOOKING_LEAD_SOURCE } from '../../utils/bookingLeadsApi';
import { memberToCallCustomer } from '../../utils/customerVoiceCall';
import s from './MemberRowActions.module.scss';

/**
 * variant="row" — after status click, status + Create Customer buttons are removed;
 * card tint shows outcome; Send WhatsApp + Delete + Edit remain.
 * variant="sheet" — full strip always.
 */
export default function MemberRowActions({
  batchId,
  member,
  onRefresh,
  showToast,
  marketingInitiated = true,
  onOpenPanel,
  onQuickCreateCustomer = null,
  showCall = false,
  showCreateCustomer = true,
  showDelete = true,
  onDelete = null,
  onRestore = null,
  disabled = false,
  onOutcomeChange = null,
  variant = 'row',
  quietStatusToasts = false,
  suppressEditButton = false,
  compactStatusStrip = false,
  followUpQueueActions = false,
  refollowMemberId = null,
  onFollowUpDone = null,
  onFollowUpCancel = null,
  batch = null,
}) {
  const [busy, setBusy] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [statusCollapsed, setStatusCollapsed] = useState(false);
  const [waSent, setWaSent] = useState(false);
  const [leadModalOpen, setLeadModalOpen] = useState(false);
  const [callModalOpen, setCallModalOpen] = useState(false);
  const [bookingQuoteOpen, setBookingQuoteOpen] = useState(false);

  const callCustomer = useMemo(() => memberToCallCustomer(member), [member]);

  const memberId = member?.id;
  const phone = member?.phone_e164 || member?.phone;
  const isDeleted = String(member?.outcome || '').toLowerCase() === 'deleted';
  const isLeadOnly = isLeadOnlyMember(member);
  const customerAlreadyCreated = String(member?.outcome || '').toLowerCase() === 'customer_created'
    || Boolean(member?.customer_id);
  const showCreateCustomerBtn = showCreateCustomer && isLeadOnly && !customerAlreadyCreated;
  const isDisabled = disabled || !!busy || isDeleted;
  const waBlocked = !canSendWhatsappForMember(member, batch);
  const waDisabled = isDisabled || waBlocked;
  const isSheet = variant === 'sheet';
  const isCompactSheet = isSheet && showCall;
  const menuUp = isSheet;
  const hasRecordedStatus = hasStatusOutcome(member?.outcome) || statusCollapsed;
  const isCollapsed = !isSheet && hasRecordedStatus && !isEditing;
  const showStatusStrip = !isCollapsed;

  useEffect(() => {
    setIsEditing(false);
    setStatusCollapsed(hasStatusOutcome(member?.outcome));
  }, [memberId, member?.outcome, member?.customer_id, member?.is_lead_only]);

  useEffect(() => {
    if (hasStatusOutcome(member?.outcome)) {
      setStatusCollapsed(true);
    }
  }, [member?.outcome, member?.customer_id]);

  useEffect(() => {
    const fromFlag = Boolean(member?.whatsapp_sent);
    const wa = member?.last_wa;
    const del = String(wa?.delivery_label || wa?.status_label || '').toLowerCase();
    const failed = del.includes('failed') || del.includes('not on whatsapp');
    const fromLog =
      wa
      && (wa.sent_at || wa.delivery_label || wa.status_label)
      && !del.includes('pending')
      && !failed;
    setWaSent((was) => was || fromFlag || fromLog);
  }, [memberId, member?.whatsapp_sent, member?.last_wa]);

  const runCreateCustomer = async () => {
    if (onQuickCreateCustomer) {
      setBusy('create_customer');
      try {
        await onQuickCreateCustomer(member);
        setConfirm(null);
      } catch (err) {
        showToast?.(err.message || 'Create customer failed', 'error');
      } finally {
        setBusy(null);
      }
      return;
    }
    onOpenPanel?.(member);
  };

  const handleSendWa = async (templateMode = 'marketing') => {
    if (!memberId || disabled) {
      if (!memberId) showToast?.('Missing batch member id — refresh the batch and try again', 'error');
      return;
    }
    setBusy('whatsapp');
    try {
      const nameHint = member?.greeting_name || member?.name || member?.business_name || '';
      const result = await sendMemberWhatsapp(batchId, memberId, templateMode, nameHint, {
        refollowMemberId: followUpQueueActions ? refollowMemberId : null,
      });
      setWaSent(true);
      onOutcomeChange?.(memberLastWaPatchFromSendResult(result, templateMode));
      onRefresh?.({ soft: !followUpQueueActions });
      const kind = result?.wa_send_kind || (result?.wa_fallback_used ? 'backup' : templateMode);
      const label = kind === 'backup' ? 'Backup sent' : kind === 'utility' ? 'Utility sent' : 'Marketing sent';
      showToast?.(`WhatsApp ${label.toLowerCase()}`, 'success');
    } catch (err) {
      const msg = err.message || 'WhatsApp send failed';
      if (msg.includes('blocked_non_prod')) showToast?.('Blocked on non-prod', 'error');
      else if (msg.includes('skipped_opt_out')) showToast?.('Contact is opted out', 'error');
      else showToast?.(msg, 'error');
      // Reload last contacts — failed sends still create marketing_logs rows.
      onRefresh?.({ soft: false });
    } finally {
      setBusy(null);
    }
  };

  const run = async (action, extra = {}) => {
    if (!memberId || disabled) {
      if (!memberId) showToast?.('Missing batch member id — refresh the batch and try again', 'error');
      return;
    }
    setBusy(action);
    try {
      const payload = {
        action,
        marketing_initiated: marketingInitiated,
        remark: extra.remark,
      };
      const result = await postMemberAction(batchId, memberId, payload);
      const serverAt = result?.event?.created_at;
      const actorRole = marketingInitiated ? 'marketing' : 'ops';
      const waPatch = memberPatchFromAutoWhatsapp(result);
      if (waPatch) setWaSent(true);
      const patch = action !== 'call' ? outcomeFromAction(action) : null;
      if (patch?.outcome) {
        onOutcomeChange?.({
          ...patch,
          outcome_at: serverAt || new Date().toISOString(),
          last_ops_at: serverAt || new Date().toISOString(),
          last_ops_role: actorRole,
          last_ops_action: action,
          ...(waPatch || {}),
        });
        if (!isSheet && hasStatusOutcome(patch.outcome)) {
          setStatusCollapsed(true);
          setIsEditing(false);
        }
      } else if (waPatch) {
        onOutcomeChange?.(waPatch);
      }
      const isStatusAction = action !== 'whatsapp' && action !== 'call';
      if (!quietStatusToasts || !isStatusAction) {
        showToast?.(
          action === 'call'
            ? 'Call initiated'
            : `${action.replace(/_/g, ' ')} recorded`,
          'success',
        );
      }
      setConfirm(null);
    } catch (err) {
      const msg = err.message || 'Action failed';
      if (msg.includes('blocked_non_prod') || msg.includes('Blocked on non-prod')) {
        showToast?.('Blocked on non-prod', 'error');
      } else if (msg.includes('skipped_opt_out')) {
        showToast?.('Contact is opted out', 'error');
      } else if (msg.includes('rate') || msg.includes('429')) {
        showToast?.('Rate limited — try again later', 'error');
      } else if (msg.includes('template') || msg.includes('unapproved')) {
        showToast?.('Template not approved or unavailable', 'error');
      } else {
        showToast?.(msg, 'error');
      }
    } finally {
      setBusy(null);
    }
  };

  const recordCallAction = async (callLogId, callKind = 'ops') => {
    if (!memberId) return;
    setBusy('call');
    try {
      const result = await postMemberAction(batchId, memberId, {
        action: 'call',
        marketing_initiated: marketingInitiated,
        skip_dial: true,
        call_log_id: callLogId ?? undefined,
        remark: callKind === 'bot' ? 'bot_call' : undefined,
      });
      if (!quietStatusToasts) showToast?.('Call initiated', 'success');
      onRefresh?.({ soft: true });
      return result;
    } catch (err) {
      showToast?.(err.message || 'Call placed but failed to log in batch', 'error');
    } finally {
      setBusy(null);
    }
  };

  const handleOpsCallSuccess = async (data) => {
    await recordCallAction(data?.call_log_id, 'ops');
  };

  const handleBotCallSuccess = async () => {
    await recordCallAction(null, 'bot');
  };

  const handleLeadCreated = async (bookingLeadId) => {
    if (!memberId) return;
    setBusy('create_lead');
    try {
      const result = await postMemberAction(batchId, memberId, {
        action: 'create_lead',
        marketing_initiated: marketingInitiated,
        booking_lead_id: bookingLeadId,
      });
      const serverAt = result?.event?.created_at;
      const actorRole = marketingInitiated ? 'marketing' : 'ops';
      const patch = outcomeFromAction('create_lead');
      onOutcomeChange?.({
        ...patch,
        outcome_at: serverAt || new Date().toISOString(),
        last_ops_at: serverAt || new Date().toISOString(),
        last_ops_role: actorRole,
        last_ops_action: 'create_lead',
        lead_id: bookingLeadId,
        booking_lead_id: bookingLeadId,
      });
      if (!isSheet && patch?.outcome && hasStatusOutcome(patch.outcome)) {
        setStatusCollapsed(true);
        setIsEditing(false);
      }
      setLeadModalOpen(false);
      if (!quietStatusToasts) showToast?.('Lead recorded', 'success');
    } catch (err) {
      showToast?.(err.message || 'Failed to record lead status', 'error');
    } finally {
      setBusy(null);
    }
  };

  const rowTone = memberRowTone(member?.outcome);
  const toneClass = {
    success: s.toneSuccess,
    followup: s.toneFollowup,
    lead: s.toneLead,
    attempted: s.toneAttempted,
    muted: s.toneMuted,
    deleted: s.toneDeleted,
    danger: s.toneDanger,
    neutral: '',
  }[rowTone] || '';

  if (followUpQueueActions) {
    return (
      <div className={`${s.root} ${s.rootPendingStrip}`} onClick={(e) => e.stopPropagation()}>
        {showCall ? (
          <button type="button" className={s.btn} disabled={isDisabled} onClick={() => setCallModalOpen(true)}>
            Call
          </button>
        ) : null}
        {onFollowUpDone ? (
          <button type="button" className={`${s.btn} ${s.btnSuccess}`} disabled={isDisabled} onClick={() => onFollowUpDone()}>
            Done
          </button>
        ) : null}
        {onFollowUpCancel ? (
          <button type="button" className={`${s.btn} ${s.btnDanger}`} disabled={isDisabled} onClick={() => onFollowUpCancel()}>
            Cancel
          </button>
        ) : null}
        <span className={s.rowBreak} aria-hidden />
        <button type="button" className={s.btn} disabled={isDisabled} onClick={() => setLeadModalOpen(true)}>
          Create lead
        </button>
        <div className={`${s.waSplit}${busy === 'whatsapp' ? ` ${s.waSplitBusy}` : ''}`}>
          <button
            type="button"
            className={`${s.btn} ${waSent ? s.btnWaSent : s.btnWa}`}
            disabled={waDisabled}
            title={
              waBlocked
                ? 'Create customer with captured WhatsApp number first'
                : waSent
                  ? 'WhatsApp sent — hover to send again (Marketing, Utility, or quote)'
                  : 'Hover to choose Marketing Ad or Utility'
            }
          >
            {busy === 'whatsapp' ? 'Sending…' : waSent ? 'WhatsApp sent' : 'Send WA'}
          </button>
          {!waDisabled && busy !== 'whatsapp' && (
            <div className={`${s.waSplitMenu}${menuUp ? ` ${s.menuUp}` : ''}`} role="menu">
              <button type="button" role="menuitem" onClick={() => handleSendWa('marketing')}>Marketing Ad</button>
              <button type="button" role="menuitem" onClick={() => handleSendWa('utility')}>Utility</button>
              <button type="button" role="menuitem" onClick={() => setBookingQuoteOpen(true)}>Booking quote</button>
            </div>
          )}
        </div>
        <CreateBookingLeadModal
          open={leadModalOpen}
          onClose={() => setLeadModalOpen(false)}
          member={member}
          leadSource={BOOKING_LEAD_SOURCE.CALL_MANAGER}
          onCreated={handleLeadCreated}
          showToast={showToast}
        />
        <CustomerVoiceCallModal
          open={callModalOpen}
          onClose={() => setCallModalOpen(false)}
          customer={callCustomer}
          onOpsCallSuccess={handleOpsCallSuccess}
          onBotCallSuccess={handleBotCallSuccess}
        />
        {!marketingInitiated ? (
          <BookingQuoteWaModal
            open={bookingQuoteOpen}
            onClose={() => setBookingQuoteOpen(false)}
            batchId={batchId}
            memberId={memberId}
            member={member}
            refollowMemberId={refollowMemberId}
            showToast={showToast}
            onSent={(result) => {
              setBookingQuoteOpen(false);
              setWaSent(true);
              onOutcomeChange?.(
                memberLastWaPatchFromSendResult(result || { wa_send_kind: 'booking_quote' }, 'booking_quote'),
              );
              onRefresh?.({ soft: true });
            }}
          />
        ) : null}
      </div>
    );
  }

  return (
    <div
      className={`${s.root}${isSheet ? ` ${s.rootSheet}` : ''}${isCompactSheet ? ` ${s.rootSheetCompact}` : ''}${compactStatusStrip && showStatusStrip ? ` ${s.rootPendingStrip}` : ''}${toneClass ? ` ${toneClass}` : ''}`}
      onClick={(e) => e.stopPropagation()}
    >
      {showCall ? (
        <button type="button" className={s.btn} disabled={isDisabled} onClick={() => setCallModalOpen(true)}>
          Call
        </button>
      ) : null}

      {showStatusStrip && !isDeleted ? (
        <>
          {showCreateCustomerBtn && !(compactStatusStrip && showStatusStrip) ? (
            <button type="button" className={`${s.btn} ${s.btnPrimary}`} disabled={isDisabled} onClick={runCreateCustomer}>
              {busy === 'create_customer' ? 'Creating…' : 'Create Customer'}
            </button>
          ) : null}
          <button type="button" className={s.btn} disabled={isDisabled} onClick={() => run('good_response')}>Good Response</button>
          <div className={`${s.fuSplit}${busy === 'follow_up' || busy === 'create_lead' ? ` ${s.fuSplitBusy}` : ''}`}>
            <button
              type="button"
              className={s.btn}
              disabled={isDisabled}
              title="Hover to choose Follow Up or Lead"
            >
              Follow Up
            </button>
            {!isDisabled && busy !== 'follow_up' && busy !== 'create_lead' && (
              <div className={`${s.fuSplitMenu}${menuUp ? ` ${s.menuUp}` : ''}`} role="menu">
                <button type="button" role="menuitem" onClick={() => run('follow_up')}>Follow Up</button>
                <button type="button" role="menuitem" onClick={() => setLeadModalOpen(true)}>Lead</button>
              </div>
            )}
          </div>
          <button type="button" className={s.btn} disabled={isDisabled} onClick={() => run('not_contacted')}>Not Picked Up</button>
          <button type="button" className={s.btn} disabled={isDisabled} onClick={() => setConfirm({ type: 'opt_out' })}>Opt Out</button>
          <button type="button" className={s.btn} disabled={isDisabled} onClick={() => run('drop')}>Drop</button>
        </>
      ) : null}

      {/* Line break between status and action buttons in pending strip */}
      {compactStatusStrip && showStatusStrip && !isDeleted ? <span className={s.rowBreak} /> : null}

      {compactStatusStrip && showStatusStrip && !isDeleted && showCreateCustomerBtn ? (
        <button type="button" className={`${s.btn} ${s.btnPrimary}`} disabled={isDisabled} onClick={runCreateCustomer}>
          {busy === 'create_customer' ? 'Creating…' : 'Create Customer'}
        </button>
      ) : null}

      <div className={`${s.waSplit}${busy === 'whatsapp' ? ` ${s.waSplitBusy}` : ''}`}>
        <button
          type="button"
          className={`${s.btn} ${waSent ? s.btnWaSent : s.btnWa}`}
          disabled={waDisabled}
          title={
            waBlocked
              ? 'Create customer with captured WhatsApp number first'
              : waSent
              ? 'WhatsApp sent — hover to send again (Marketing, Utility, or quote)'
              : 'Hover to choose Marketing Ad or Utility'
          }
        >
          {busy === 'whatsapp' ? 'Sending…' : waSent ? 'WhatsApp sent' : isCompactSheet ? 'WhatsApp' : 'Send WA'}
        </button>
        {!waDisabled && busy !== 'whatsapp' && (
          <div className={`${s.waSplitMenu}${menuUp ? ` ${s.menuUp}` : ''}`} role="menu">
            <button type="button" role="menuitem" onClick={() => handleSendWa('marketing')}>
              Marketing Ad
            </button>
            <button type="button" role="menuitem" onClick={() => handleSendWa('utility')}>
              Utility
            </button>
            {!marketingInitiated ? (
              <button type="button" role="menuitem" onClick={() => setBookingQuoteOpen(true)}>
                Booking quote
              </button>
            ) : null}
          </div>
        )}
      </div>

      {!marketingInitiated ? (
        <BookingQuoteWaModal
          open={bookingQuoteOpen}
          onClose={() => setBookingQuoteOpen(false)}
          batchId={batchId}
          memberId={memberId}
          member={member}
          marketingInitiated={false}
          showToast={showToast}
          onSent={(result) => {
            setWaSent(true);
            onOutcomeChange?.(memberLastWaPatchFromSendResult(result || { wa_send_kind: 'booking_quote' }, 'utility'));
            onRefresh?.({ soft: true });
          }}
          onBookingComplete={() => onRefresh?.({ soft: true })}
        />
      ) : null}

      {showDelete && onDelete && !isDeleted ? (
        <button
          type="button"
          className={`${s.btn} ${s.btnDanger}`}
          disabled={isDisabled}
          title="Soft-delete from batch"
          onClick={() => onDelete(member)}
        >
          <Trash2 size={14} />
          Delete
        </button>
      ) : null}

      {isDeleted && onRestore ? (
        <button
          type="button"
          className={`${s.btn} ${s.btnRestore}`}
          disabled={!!busy}
          title="Restore member"
          onClick={() => onRestore(member)}
        >
          <RotateCcw size={14} />
          Reimport
        </button>
      ) : null}

      {isCollapsed && !suppressEditButton ? (
        <button
          type="button"
          className={`${s.btn} ${s.btnEdit}`}
          disabled={isDisabled}
          onClick={() => setIsEditing(true)}
        >
          <Pencil size={14} style={{ verticalAlign: -2, marginRight: 4 }} />
          Edit
        </button>
      ) : null}

      <CustomerVoiceCallModal
        open={callModalOpen}
        customer={callCustomer}
        onClose={() => setCallModalOpen(false)}
        onOpsCallSuccess={handleOpsCallSuccess}
        onBotCallSuccess={handleBotCallSuccess}
      />

      {confirm?.type === 'opt_out' && (
        <div className={s.modalBackdrop} onClick={() => setConfirm(null)}>
          <div className={s.modal} onClick={(e) => e.stopPropagation()}>
            <h4>Opt out</h4>
            <p>This will opt out {phone} from all future marketing.</p>
            <div className={s.modalActions}>
              <button type="button" className={s.btn} onClick={() => setConfirm(null)}>Cancel</button>
              <button type="button" className={`${s.btn} ${s.btnDanger}`} onClick={() => run('opt_out')}>Confirm opt out</button>
            </div>
          </div>
        </div>
      )}

      <CreateBookingLeadModal
        open={leadModalOpen}
        onClose={() => setLeadModalOpen(false)}
        member={member}
        leadSource={marketingInitiated ? BOOKING_LEAD_SOURCE.MARKETING : BOOKING_LEAD_SOURCE.CALL_MANAGER}
        onCreated={handleLeadCreated}
        showToast={showToast}
      />
    </div>
  );
}
